import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { getItemApi } from '../../../apiUtils';
import { TasksReportDataGrid } from '../../../components/datagrid/TasksReportDataGrid';
import { DatagridNoResponsiveContainer } from '../../../components/datagridNoResponsive/DatagridNoResponsiveContainer';
import { Loader } from '../../../components/loader/Loader';
import { useBreadcrumbs } from '../../../contexts/breadcrumbsContext';
import { useTranslation } from '../../../contexts/translation';
import TaskReportFilter from '../../../forms/filter/taskReport/TaskReportFilter';
import { useIsTaskCreatePermission } from '../../../hooks/useIsTaskCreatePermission';
import { useSaveDataGridFilter } from '../../../hooks/useSaveDataGridFilter';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { Task } from '../../../types/task.types';
import { UserColor } from '../../../types/userColor.types';
import { API_URL, fetcher } from '../../../utils/apiUrl';
import { scrollDatagridNoResponsive } from '../../../utils/datagridNoResponsive';
import { STORAGE_KEYS } from '../../../utils/localStorageKeys';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import { ReportType } from '../../../utils/reportTypeKeys';
import { swrConfig } from '../../Assets/AssetsEdit';
import { tabsIndexes } from '../../Tasks/TasksEdit';
import '../Reports.scss';

export function TaskReport() {
  const [data, setData] = useState<Array<Task> | null>([]);
  const [isFilterVisible, setFilterVisible] = useState<boolean>(true);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [loadSettingFromSession, setLoadSettingFromSession] = useState(false);
  const { translate } = useTranslation();
  const { pathname } = useLocation();
  const settingId = pathname.split('/')[3];
  const transFormName = 'Task.List';
  const { setValue } = useBreadcrumbs();
  const [refetchData, setRefetchData] = useState<boolean>(false);
  const [isDatagridFilterActive, setIsDatagridFilterActive] = useState<boolean>(false);

  useEffect(() => {
    setValue({ pageName: 'Tasks', steps: [{ name: 'Home', pathToCutCount: 1 }] });
    return () => {
      setValue(null);
    };
  }, []);

  const reportType = ReportType.Tasks;

  const history = useHistory();
  // Načtení práv uživatele
  const [userHasPermissionToCreate, loadingPermission] = useIsTaskCreatePermission();
  // Reference na dataGrid. Nutné např. kvůli ukládání nastavení.
  const dataGridRef = useRef<any>(null);
  // Indikátor, zda se má provést reset filtru DataGridu i filtru mimo DataGrid
  const [resetFilterAction, setResetFilterAction] = useState<boolean>(false);

  const {
    getActualJsonState,
    loadFilter,
    resetFilter,
    saveFilter,
    data: datagridJsonData,
  } = useSaveDataGridFilter(reportType.key, dataGridRef);

  const [datagridSetting] = useSessionStorage(STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP, {});

  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  // Trigger scroll
  useEffect(() => {
    setTimeout(() => {
      scrollDatagridNoResponsive();
    }, 1000);
  }, []);

  useEffect(() => {
    const navigatedAway = sessionStorage.getItem(
      `${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`,
    );
    if (navigatedAway) {
      // Návrat zpět – zachovat datagrid session, flag smazat
      sessionStorage.removeItem(`${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`);
      setLoadSettingFromSession(true);
      loadFilter(datagridSetting);
    } else {
      // Přímá navigace – smazat datagrid session, DB nastavení má přednost
      sessionStorage.removeItem(STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP);
      setLoadSettingFromSession(false);
    }
  }, []);

  useEffect(() => {
    if (resetFilterAction) {
      resetFilter();
      setResetFilterAction(false);
    }
    saveFilter();

    if (!dataGridRef.current || !dataGridRef.current.instance) return;
  }, [resetFilterAction]);

  /**
   * Handle editačního tlačítka v DataGridu
   * @param e  Event
   */
  const handleEditButton = (e): void => {
    if (!isNilOrEmpty(e.row) && !isNilOrEmpty(e.row.data)) {
      sessionStorage.setItem(`${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`, '1');
      history.push({
        pathname: `/tasks/edit/${e.row.data.id}`,
        state: { backRoute: pathname },
      });
    }
  };

  /**
   * Handle tlačítka pro vytvoření nového výkazu v DataGridu
   * @param e  Event
   */
  const handleCreateWorksheetButton = (e): void => {
    if (isNilOrEmpty(e.row) || isNilOrEmpty(e.row.data)) return;

    // zjistíme počet výkazů - WORKSHEET_GET_COUNT_BY_TASK
    getItemApi<number>({
      url: `${API_URL.WORKSHEET_GET_COUNT_BY_TASK}?parentId=${e.row.data.id}`,
      params: {},
      callAfterSuccess: (result: number) => {
        // neexistuje výkaz, přesměrujeme na create
        if (result === 0) {
          sessionStorage.setItem(
            `${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`,
            '1',
          );
          history.push({
            pathname: '/workSheets/new/0/' + e.row.data.id,
            state: { backRoute: pathname },
          });
          return;
        }

        // jinak přesměrujeme na seznam výkazů v detailu tasku
        sessionStorage.setItem(`${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`, '1');
        history.push({
          pathname: `/tasks/edit/${e.row.data.id}`,
          state: { defaultTabIndex: tabsIndexes.WORKSHEETS, backRoute: pathname },
        });
      },
      hideNotifications: true,
    });
  };

  /**
   * Handle kopírovacího tlačítka v DataGridu
   * @param e  Event
   */
  const handleCopyButton = (e): void => {
    if (!isNilOrEmpty(e.row) && !isNilOrEmpty(e.row.data)) {
      sessionStorage.setItem(`${STORAGE_KEYS.TASKS_REPORT_DATAGRID_DESKTOP}_navigated_away`, '1');
      history.push({ pathname: `/tasks/new/${e.row.data.id}`, state: { backRoute: pathname } });
    }
  };

  /**
   * Po změně akce refetchnout data
   */
  const mutateReportData = () => {
    setRefetchData(true);
  };

  if (!data || !userColors) return <Loader />;
  return (
    <DatagridNoResponsiveContainer>
      <TaskReportFilter
        dataGridRef={dataGridRef}
        onDataChange={setData}
        report={reportType}
        onFilterChange={setSelectedFilter}
        filterVisibility={isFilterVisible}
        loadDefaultSetting={!loadSettingFromSession}
        heading={isFilterVisible ? translate!('Tasks', transFormName) : ''}
        refetchData={refetchData}
        setRefetchData={setRefetchData}
      />
      {isDatagridFilterActive && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span className="datagrid-active-filter">
            {translate!('You have active filter', 'Reports.Bar')}
          </span>
        </div>
      )}
      <TasksReportDataGrid
        datagridJsonData={datagridSetting}
        dataGridRef={dataGridRef}
        data={data.map((x) => {
          // @ts-ignore
          return { ...x, worksheetTotalTime2: x.worksheetTotalTime };
        })}
        resetFilter={() => setResetFilterAction(true)}
        userCanCreate={userHasPermissionToCreate}
        dataGridKey={reportType.key}
        handleEdit={handleEditButton}
        handleCreateWorksheet={handleCreateWorksheetButton}
        handleCopy={handleCopyButton}
        heading={`${translate!('Tasks', transFormName)}${
          selectedFilter ? ` - ${selectedFilter}` : ''
        }`}
        mutateData={mutateReportData}
        filterVisibility={isFilterVisible}
        setFilterVisibility={setFilterVisible}
        userColors={userColors}
        filterChanged={(hasFilter) => {
          setIsDatagridFilterActive(hasFilter);
        }}
        noTopPadding={isDatagridFilterActive}
      />
    </DatagridNoResponsiveContainer>
  );
}
export default forwardRef(TaskReport);
